// =============================================================================
// Form state — sessionStorage serialization for the wizard.
//
// The wizard spans three HTML pages (step-1, step-2, results). State is held
// in sessionStorage per tab. Cleared when the tab closes. No persistence
// across sessions; no PII in the URL.
//
// Step 2 collects lab values in the user's chosen unit system. This module
// normalizes to the engine's internal units (μg/L, g/L, μmol/L, etc.) before
// storing. The engine never sees raw US-unit values.
// =============================================================================

import type { LabInputs, LifeStage, UnitSystem } from '../engine/types.js';

/** Storage key — bump version when the shape of stored data changes. */
const STORAGE_KEY = 'ff-lab-inputs-v1';

/** What we serialize to sessionStorage. Matches LabInputs + active unit system. */
interface StoredInputs extends LabInputs {
  /** Unit system the user last had active in step 2. Retained for display, not interpretation. */
  unitSystem?: UnitSystem;
}

// -----------------------------------------------------------------------------
// Unit conversion — US → SI (internal)
// -----------------------------------------------------------------------------

/** Hemoglobin: g/dL → g/L. */
export function hbUsToSI(gDL: number): number {
  return gDL * 10;
}

/** Serum iron / TIBC: μg/dL → μmol/L (× 0.179). */
export function ironUsToSI(ugDL: number): number {
  return ugDL * 0.179;
}

/**
 * Convert a raw step-2 input value to the engine's internal unit system,
 * given the unit system the user was in when they typed it.
 */
export function normalizeToInternal(
  field: keyof LabInputs,
  value: number,
  unitSystem: UnitSystem,
): number {
  if (unitSystem === 'si') return value;
  switch (field) {
    case 'hemoglobin':
      return hbUsToSI(value);
    case 'serumIron':
    case 'tibc':
      return ironUsToSI(value);
    // ferritin (μg/L ≡ ng/mL numerically), tsat, mcv, mch, rdw, crp, stfr, retHe:
    // no conversion required.
    default:
      return value;
  }
}

// -----------------------------------------------------------------------------
// Life-stage DOM mapping
// -----------------------------------------------------------------------------

/** Map the step-1 radio values to LabInputs.lifeStage. */
function mapLifeStageRadio(value: string | null): LifeStage {
  switch (value) {
    case 'cycling':
      return 'non_pregnant_premenopausal';
    case 'pregnant':
      // Trimester refined at save-time from the due-date field if supplied.
      return 'pregnant_t2';
    case 'postpartum':
      return 'postpartum_within_6mo';
    case 'perimenopausal':
      return 'perimenopausal';
    case 'postmenopausal':
      return 'postmenopausal';
    case 'none':
    default:
      return 'none_of_these';
  }
}

/** If user is pregnant + provided a due-date, derive trimester from weeks-out. */
function trimesterFromDueDate(dueISO: string | null): LifeStage {
  if (!dueISO) return 'pregnant_t2';
  const due = new Date(dueISO);
  if (Number.isNaN(due.getTime())) return 'pregnant_t2';
  const now = new Date();
  const weeksUntilDue = Math.round((due.getTime() - now.getTime()) / (7 * 24 * 60 * 60 * 1000));
  // 40-week term. T1 ≈ 40–27 weeks out; T2 ≈ 26–14; T3 ≈ ≤13.
  if (weeksUntilDue > 27) return 'pregnant_t1';
  if (weeksUntilDue > 13) return 'pregnant_t2';
  return 'pregnant_t3';
}

// -----------------------------------------------------------------------------
// Readers — pull values out of the live DOM
// -----------------------------------------------------------------------------

function readRadio(name: string, doc: Document = document): string | null {
  const el = doc.querySelector<HTMLInputElement>(`input[name="${name}"]:checked`);
  return el ? el.value : null;
}

function readNumber(id: string, doc: Document = document): number | undefined {
  const el = doc.getElementById(id) as HTMLInputElement | null;
  if (!el) return undefined;
  const raw = el.value.trim();
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function readCheckboxGroup(name: string, doc: Document = document): string[] {
  return Array.from(
    doc.querySelectorAll<HTMLInputElement>(`input[name="${name}"]:checked`),
  ).map((el) => el.value);
}

// -----------------------------------------------------------------------------
// Step 1 — demographics, life stage, diet, optional context
// -----------------------------------------------------------------------------

export interface Step1Snapshot {
  age: number;
  referenceRange: 'male' | 'female';
  lifeStage: LifeStage;
  gaht: LabInputs['gaht'];
  diet: LabInputs['diet'];
  recentIllness2Weeks?: boolean;
  ironSupplementation?: LabInputs['ironSupplementation'];
}

export function readStep1(doc: Document = document): Step1Snapshot | null {
  const age = readNumber('age', doc);
  const lifeStageRaw = readRadio('life-stage', doc);
  if (age === undefined) return null;

  // The tool scopes to female physiology — the male reference-range option
  // was removed because the corpus is built on research about women's iron
  // health. Hardcoded here so downstream threshold selection stays intact.
  const referenceRange: 'male' | 'female' = 'female';

  const baseStage = mapLifeStageRadio(lifeStageRaw);
  const lifeStage =
    baseStage === 'pregnant_t2'
      ? trimesterFromDueDate((doc.getElementById('due') as HTMLInputElement | null)?.value ?? null)
      : baseStage;

  // GAHT: step-1 asks "what kind of hormone therapy are you on?"
  //   testosterone → masculinizing_*   (out-of-scope at 12+ months)
  //   estrogen     → feminizing_*
  //   none         → 'none'
  // Duration (12plus / under12) is only shown when the user picks testosterone
  // or estrogen. Absent duration with a GAHT type selected → treat as 'under12'
  // to be conservative (caveat banner instead of out-of-scope).
  const gahtType = readRadio('gaht-type', doc);
  const gahtDuration = readRadio('gaht-duration', doc);
  const gaht: LabInputs['gaht'] = (() => {
    if (gahtType !== 'testosterone' && gahtType !== 'estrogen') return 'none';
    const duration = gahtDuration === '12plus' ? '12plus' : 'under12';
    if (gahtType === 'testosterone') {
      return duration === '12plus' ? 'masculinizing_12plus' : 'masculinizing_under_12';
    }
    return duration === '12plus' ? 'feminizing_12plus' : 'feminizing_under_12';
  })();

  const dietRaw = readRadio('diet', doc);
  const diet: LabInputs['diet'] = dietRaw === 'plant-forward' ? 'plant_forward' : 'omnivore';

  const recentIllnessRaw = readRadio('recent-illness', doc);
  const ironSuppRaw = readRadio('iron-supp', doc);

  const snapshot: Step1Snapshot = {
    age,
    referenceRange,
    lifeStage,
    gaht,
    diet,
  };
  if (recentIllnessRaw === 'yes') snapshot.recentIllness2Weeks = true;
  else if (recentIllnessRaw === 'no') snapshot.recentIllness2Weeks = false;

  if (ironSuppRaw === 'current') snapshot.ironSupplementation = 'currently';
  else if (ironSuppRaw === 'recent') snapshot.ironSupplementation = 'stopped_recent';
  else if (ironSuppRaw === 'no') snapshot.ironSupplementation = 'no';

  return snapshot;
}

// -----------------------------------------------------------------------------
// Step 2 — lab values + symptoms + lifestyle context
// -----------------------------------------------------------------------------

export interface Step2Snapshot {
  unitSystem: UnitSystem;
  ferritin?: number;
  hemoglobin?: number;
  mcv?: number;
  mch?: number;
  rdw?: number;
  tsat?: number;
  serumIron?: number;
  tibc?: number;
  crp?: number;
  stfr?: number;
  retHe?: number;
  symptoms?: LabInputs['symptoms'];
  coffeeTeaHabit?: LabInputs['coffeeTeaHabit'];
  testDate?: string;
}

/** Detect the currently active unit toggle. Defaults to SI when absent. */
export function readUnitSystem(doc: Document = document): UnitSystem {
  const active = doc.querySelector<HTMLButtonElement>('[data-unit].is-active');
  const val = active?.getAttribute('data-unit');
  return val === 'us' ? 'us' : 'si';
}

export function readStep2(doc: Document = document): Step2Snapshot {
  const unitSystem = readUnitSystem(doc);

  const s: Step2Snapshot = { unitSystem };

  const pairs: Array<[string, keyof Step2Snapshot]> = [
    ['hb', 'hemoglobin'],
    ['mcv', 'mcv'],
    ['mch', 'mch'],
    ['rdw', 'rdw'],
    ['ferritin', 'ferritin'],
    ['tsat', 'tsat'],
    ['serum-iron', 'serumIron'],
    ['tibc', 'tibc'],
    ['stfr', 'stfr'],
    ['crp', 'crp'],
    ['rethe', 'retHe'],
  ];
  for (const [domId, field] of pairs) {
    const raw = readNumber(domId, doc);
    if (raw !== undefined) {
      // Normalize to internal units at capture time.
      (s as unknown as Record<string, number>)[field as string] = normalizeToInternal(
        field as keyof LabInputs,
        raw,
        unitSystem,
      );
    }
  }

  // Symptoms checkboxes use value strings that match the Symptom union in types.ts.
  const syms = readCheckboxGroup('sym', doc);
  if (syms.length) {
    const mapped = syms
      .map((v) => {
        switch (v) {
          case 'fatigue':
            return 'unusual_fatigue';
          case 'hair':
            return 'hair_shedding';
          case 'nails':
            return 'brittle_nails';
          case 'rls':
            return 'restless_legs';
          case 'pica':
            return 'pica';
          case 'cold':
            return 'cold_intolerance';
          default:
            return undefined;
        }
      })
      .filter((x): x is NonNullable<LabInputs['symptoms']>[number] => x !== undefined);
    if (mapped.length) s.symptoms = mapped;
  }

  const coffee = readRadio('coffee', doc);
  if (coffee === 'yes-meals') s.coffeeTeaHabit = 'with_meals';
  else if (coffee === 'yes-not-meals') s.coffeeTeaHabit = 'not_with_meals';
  else if (coffee === 'no') s.coffeeTeaHabit = 'no_rare';

  const drawDate = (doc.getElementById('draw-date') as HTMLInputElement | null)?.value;
  if (drawDate) s.testDate = drawDate;

  return s;
}

// -----------------------------------------------------------------------------
// sessionStorage plumbing
// -----------------------------------------------------------------------------

export function saveStep1(snapshot: Step1Snapshot): void {
  const existing = load() ?? {};
  const merged: StoredInputs = {
    ...existing,
    ...snapshot,
  } as StoredInputs;
  write(merged);
}

export function saveStep2(snapshot: Step2Snapshot): void {
  const existing = load() ?? {};
  const merged: StoredInputs = {
    ...existing,
    ...snapshot,
  } as StoredInputs;
  write(merged);
}

export function load(): StoredInputs | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredInputs;
    return parsed;
  } catch {
    return null;
  }
}

export function clear(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage may be disabled in private mode / SSR; fail silent.
  }
}

// -----------------------------------------------------------------------------
// Restoration — populate the DOM from sessionStorage when the user returns to
// a wizard step (e.g., clicking "Back" on step 2).
// -----------------------------------------------------------------------------

/** Map an internal LifeStage back to the step-1 radio value the user clicked. */
function lifeStageToRadioValue(ls: LifeStage): string | null {
  switch (ls) {
    case 'non_pregnant_premenopausal':
      return 'cycling';
    case 'pregnant_t1':
    case 'pregnant_t2':
    case 'pregnant_t3':
      return 'pregnant';
    case 'postpartum_within_6mo':
      return 'postpartum';
    case 'perimenopausal':
      return 'perimenopausal';
    case 'postmenopausal':
      return 'postmenopausal';
    case 'none_of_these':
      return 'none';
    default:
      return null;
  }
}

function setRadio(name: string, value: string, doc: Document = document): void {
  const el = doc.querySelector<HTMLInputElement>(
    `input[name="${name}"][value="${value}"]`,
  );
  if (!el) return;
  el.checked = true;
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

function setNumber(id: string, value: number, doc: Document = document): void {
  const el = doc.getElementById(id) as HTMLInputElement | null;
  if (!el) return;
  el.value = String(value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

function setText(id: string, value: string, doc: Document = document): void {
  const el = doc.getElementById(id) as HTMLInputElement | null;
  if (!el) return;
  el.value = value;
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Populate step-1 form fields from sessionStorage. Called on page load so
 * clicking "Back" from step 2 does not lose the user's inputs.
 */
export function restoreStep1(doc: Document = document): void {
  const stored = load();
  if (!stored) return;

  if (stored.age !== undefined) setNumber('age', stored.age, doc);

  // referenceRange is no longer a UI field (always 'female'); nothing to restore.

  if (stored.lifeStage) {
    const v = lifeStageToRadioValue(stored.lifeStage);
    if (v) setRadio('life-stage', v, doc);
  }

  // GAHT → restore the type radio + the duration radio + reveal the duration
  // sub-field. 'none' in storage maps to the "No" radio being checked.
  if (stored.gaht) {
    if (stored.gaht === 'none') {
      setRadio('gaht-type', 'none', doc);
    } else {
      const type = stored.gaht.startsWith('masculinizing') ? 'testosterone' : 'estrogen';
      const duration = stored.gaht.endsWith('_12plus') ? '12plus' : 'under12';
      setRadio('gaht-type', type, doc);
      setRadio('gaht-duration', duration, doc);
      const container = doc.querySelector<HTMLElement>('[data-gaht-duration]');
      if (container) container.style.display = 'block';
    }
  }

  if (stored.diet) {
    setRadio('diet', stored.diet === 'plant_forward' ? 'plant-forward' : 'omnivore', doc);
  }

  if (stored.recentIllness2Weeks !== undefined) {
    setRadio('recent-illness', stored.recentIllness2Weeks ? 'yes' : 'no', doc);
  }

  if (stored.ironSupplementation) {
    const v =
      stored.ironSupplementation === 'currently'
        ? 'current'
        : stored.ironSupplementation === 'stopped_recent'
          ? 'recent'
          : 'no';
    setRadio('iron-supp', v, doc);
  }
}

/**
 * Populate step-2 form fields from sessionStorage. Called on page load so
 * clicking "Back" from results does not lose the user's lab values. Converts
 * internal (SI) values back to the user's preferred unit system for display.
 */
export function restoreStep2(doc: Document = document): void {
  const stored = load();
  if (!stored) return;

  const unitSystem: UnitSystem = stored.unitSystem === 'us' ? 'us' : 'si';

  // Activate the unit-toggle button for the stored unit system.
  const toggleBtns = doc.querySelectorAll<HTMLButtonElement>('[data-unit]');
  toggleBtns.forEach((b) => {
    const isTarget = b.getAttribute('data-unit') === unitSystem;
    b.classList.toggle('is-active', isTarget);
  });

  // Convert an internal SI value back to the display unit system.
  function toDisplay(field: keyof LabInputs, v: number): number {
    if (unitSystem === 'si') return v;
    switch (field) {
      case 'hemoglobin':
        return v / 10;
      case 'serumIron':
      case 'tibc':
        return v / 0.179;
      default:
        return v;
    }
  }

  const pairs: Array<[string, keyof LabInputs]> = [
    ['hb', 'hemoglobin'],
    ['mcv', 'mcv'],
    ['mch', 'mch'],
    ['rdw', 'rdw'],
    ['ferritin', 'ferritin'],
    ['tsat', 'tsat'],
    ['serum-iron', 'serumIron'],
    ['tibc', 'tibc'],
    ['stfr', 'stfr'],
    ['crp', 'crp'],
    ['rethe', 'retHe'],
  ];
  for (const [domId, field] of pairs) {
    const raw = (stored as unknown as Record<string, number | undefined>)[field as string];
    if (typeof raw === 'number') {
      const display = toDisplay(field, raw);
      // Trim trailing zeros for integer-like values to keep inputs clean.
      const pretty =
        Number.isInteger(display) ? String(display) : String(Number(display.toFixed(2)));
      setText(domId, pretty, doc);
    }
  }

  // Symptoms — check the matching checkboxes.
  if (stored.symptoms && stored.symptoms.length) {
    const symMap: Record<string, string> = {
      unusual_fatigue: 'fatigue',
      hair_shedding: 'hair',
      brittle_nails: 'nails',
      restless_legs: 'rls',
      pica: 'pica',
      cold_intolerance: 'cold',
    };
    for (const s of stored.symptoms) {
      const v = symMap[s];
      if (!v) continue;
      const el = doc.querySelector<HTMLInputElement>(`input[name="sym"][value="${v}"]`);
      if (el) el.checked = true;
    }
  }

  if (stored.coffeeTeaHabit) {
    const v =
      stored.coffeeTeaHabit === 'with_meals'
        ? 'yes-meals'
        : stored.coffeeTeaHabit === 'not_with_meals'
          ? 'yes-not-meals'
          : 'no';
    setRadio('coffee', v, doc);
  }

  if (stored.testDate) setText('draw-date', stored.testDate, doc);
}

function write(value: StoredInputs): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage quota or disabled — fall through; downstream readers handle null.
  }
}

/**
 * True when the stored state has enough of step 1 completed to pass to
 * classify() as a LabInputs. (Engine requires age, referenceRange, lifeStage,
 * gaht, diet.)
 */
export function isClassifiable(value: StoredInputs | null): value is LabInputs {
  if (!value) return false;
  return (
    typeof value.age === 'number' &&
    !!value.referenceRange &&
    !!value.lifeStage &&
    !!value.gaht &&
    !!value.diet
  );
}
